import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Search, Loader2, Zap, ClipboardPaste, X, ShieldCheck, Gauge, Layers, Code2, Phone, Mail, Crown, Infinity as InfinityIcon } from "lucide-react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { detectPlatform } from "@/lib/utils-vg";
import { LangProvider, useLang } from "@/lib/i18n";
import { Navbar } from "@/components/Navbar";
import { AuthModal } from "@/components/AuthModal";
import { VideoPreview } from "@/components/VideoPreview";
import { HistoryPanel } from "@/components/HistoryPanel";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AdSlot } from "@/components/AdSlot";
import { ChatAssistant } from "@/components/ChatAssistant";
import AdminDashboard from "@/pages/AdminDashboard";

const Shell = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [url, setUrl] = useState("");
  const [info, setInfo] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [usage, setUsage] = useState(null);

  const platform = detectPlatform(url);
  const isAuthed = user && typeof user === "object";
  const isPremium = isAuthed && user.is_premium;

  const loadHistory = useCallback(async () => {
    if (!(user && typeof user === "object")) {
      setHistory([]);
      setUsage(null);
      return;
    }
    try {
      const [h, u] = await Promise.all([api.get("/history"), api.get("/usage")]);
      setHistory(h.data);
      setUsage(u.data);
    } catch (err) {
      console.error("Failed to load history/usage:", err);
    }
  }, [user]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const openAuth = (tab) => {
    setAuthTab(tab);
    setAuthOpen(true);
  };

  const requireAuth = () => {
    if (!isAuthed) {
      openAuth("login");
      toast.info(t("toast.loginToDownload"));
      return true;
    }
    return false;
  };

  const handleLimitReached = () => {
    toast.error(t("toast.limitReached"));
    setUpgradeOpen(true);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setUrl(text);
        toast.success(t("toast.linkPasted"));
      }
    } catch {
      toast.error(t("toast.clipboardBlocked"));
    }
  };

  const handleFetch = async (e) => {
    e && e.preventDefault();
    if (!url.trim()) return;
    if (!detectPlatform(url)) {
      toast.error(t("toast.invalidUrl"));
      return;
    }
    setFetching(true);
    setInfo(null);
    try {
      const { data } = await api.post("/video/info", { url: url.trim() });
      setInfo(data);
      toast.success(t("toast.videoFound"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.fetchFail"));
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="min-h-screen vg-radial">
      <Navbar
        onLogin={() => openAuth("login")}
        onRegister={() => openAuth("register")}
        onOpenHistory={() => {
          if (requireAuth()) return;
          setHistoryOpen(true);
        }}
        historyCount={history.length}
        onUpgrade={() => {
          if (requireAuth()) return;
          setUpgradeOpen(true);
        }}
      />

      <main className="max-w-6xl mx-auto px-4 lg:px-8 pb-24">
        {/* Hero */}
        <section className="max-w-4xl mx-auto pt-12 sm:pt-16 pb-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 mb-6"
          >
            <Zap size={13} className="text-cyan-400" /> {t("hero.badge")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-head font-extrabold tracking-tight leading-[1.05]"
          >
            {t("hero.h1a")} <br className="hidden sm:block" />
            {t("hero.h1b")} <span className="vg-gradient-text">{t("hero.h1c")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm sm:text-base text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>

          {/* Link input */}
          <motion.form
            onSubmit={handleFetch}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-9"
          >
            <div className="vg-glass rounded-2xl p-2 flex flex-col sm:flex-row items-stretch gap-2 vg-glow">
              <div className="relative flex-1 flex items-center">
                {platform && (
                  <span className="absolute left-3 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-violet-600/20 text-violet-300 border border-violet-500/30">
                    {platform}
                  </span>
                )}
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("input.placeholder")}
                  className={`w-full bg-transparent outline-none text-white placeholder:text-slate-500 py-3 pr-9 text-sm sm:text-base ${
                    platform ? "pl-[86px]" : "pl-4"
                  }`}
                  data-testid="video-url-input"
                />
                {url && (
                  <button
                    type="button"
                    onClick={() => {
                      setUrl("");
                      setInfo(null);
                    }}
                    className="absolute right-2 text-slate-500 hover:text-white"
                    data-testid="clear-input-button"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={handlePaste}
                className="flex items-center justify-center gap-1.5 px-4 h-12 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10 transition-colors"
                data-testid="paste-clipboard-button"
              >
                <ClipboardPaste size={16} /> {t("btn.paste")}
              </button>

              <button
                type="submit"
                disabled={fetching || !url.trim()}
                className="flex items-center justify-center gap-2 px-6 h-12 rounded-xl bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-500 font-semibold text-white hover:opacity-95 transition-opacity disabled:opacity-60"
                data-testid="fetch-video-button"
              >
                {fetching ? <Loader2 size={18} className="vg-spin" /> : <Search size={18} />}
                {t("btn.fetch")}
              </button>
            </div>
          </motion.form>

          {isAuthed && usage && (
            <div className="mt-4 flex justify-center" data-testid="usage-badge">
              {isPremium ? (
                <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400/15 to-orange-500/15 border border-amber-500/30 text-amber-300">
                  <Crown size={13} /> {t("usage.premiumActive")} · {t("usage.unlimited")}
                </span>
              ) : (
                <button
                  onClick={() => setUpgradeOpen(true)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:border-violet-500/40 transition-colors"
                >
                  <span className={usage.remaining === 0 ? "text-rose-400 font-semibold" : "text-violet-300 font-semibold"}>
                    {usage.remaining}
                  </span>
                  <span className="text-slate-400">/ {usage.limit} {t("usage.downloadsLeft")}</span>
                  <Crown size={12} className="text-amber-400 ml-1" />
                </button>
              )}
            </div>
          )}
        </section>

        {/* Preview or features */}
        {info ? (
          <VideoPreview info={info} onDownloaded={loadHistory} requireAuth={requireAuth} onLimitReached={handleLimitReached} isPremium={isPremium} onUpgrade={() => (requireAuth() ? null : setUpgradeOpen(true))} />
        ) : (
          <section className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mt-14">
            {[
              { icon: Layers, title: t("feat.merged.t"), desc: t("feat.merged.d") },
              { icon: Gauge, title: t("feat.quality.t"), desc: t("feat.quality.d") },
              { icon: ShieldCheck, title: t("feat.safe.t"), desc: t("feat.safe.d") },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="vg-glass rounded-2xl p-5 text-left"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-600/30 to-cyan-500/20 border border-white/10 grid place-items-center mb-3">
                  <f.icon size={18} className="text-violet-300" />
                </div>
                <h4 className="font-head font-semibold text-white">{f.title}</h4>
                <p className="text-sm text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </section>
        )}

        {/* Ad slot (free users only) */}
        {!isPremium && (
          <div className="max-w-3xl mx-auto mt-10">
            <AdSlot testid="ad-slot-home" />
          </div>
        )}

        {/* Pricing CTA */}
        <section className="max-w-3xl mx-auto mt-14" data-testid="pricing-section">
          <div className="relative rounded-3xl p-[1.5px] bg-gradient-to-br from-violet-500/60 via-indigo-500/30 to-amber-400/60">
            <div className="rounded-3xl bg-[#0C0E15] px-6 py-8 sm:px-10 flex flex-col sm:flex-row items-center justify-between gap-5 relative overflow-hidden">
              <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
              <div className="text-center sm:text-left relative">
                <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-amber-400 mb-2">
                  <Crown size={13} /> {t("plan.premium")}
                </div>
                <h3 className="text-2xl font-head font-extrabold">{t("pricing.title")}</h3>
                <p className="text-sm text-slate-400 mt-1">{t("pricing.subtitle")}</p>
                <div className="flex items-center gap-4 mt-4 justify-center sm:justify-start">
                  <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
                    <InfinityIcon size={15} className="text-violet-400" /> {t("usage.unlimited")}
                  </span>
                  <span className="text-2xl font-head font-bold">
                    ₹49<span className="text-xs font-normal text-slate-400">/{t("plan.month")}</span>
                  </span>
                </div>
              </div>
              <button
                onClick={() => (requireAuth() ? null : setUpgradeOpen(true))}
                className="shrink-0 flex items-center gap-2 px-6 h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 font-semibold text-black hover:opacity-95 transition-opacity"
                data-testid="pricing-cta-button"
              >
                <Crown size={16} /> {t("pricing.cta")}
              </button>
            </div>
          </div>
        </section>

        <p className="text-center text-xs text-slate-600 mt-16 max-w-xl mx-auto leading-relaxed">
          {t("disclaimer")}
        </p>
      </main>

      {/* About / Developer Footer */}
      <footer className="border-t border-white/10 mt-8" data-testid="about-footer">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12">
          {/* About */}
          <div className="vg-glass rounded-2xl p-6 sm:p-8 text-center mb-6">
            <img
              src="/vidgrab-logo.png"
              alt="VidGrab 3D logo"
              className="h-24 w-24 mx-auto mb-4 rounded-2xl object-cover vg-glow"
              data-testid="vidgrab-logo-3d"
            />
            <h3 className="font-head font-bold text-2xl text-white">
              Vid<span className="vg-gradient-text">Grab</span>
            </h3>
            <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto leading-relaxed">{t("about.desc")}</p>
          </div>

          {/* Developer card — unique style */}
          <div
            className="relative max-w-xl mx-auto rounded-3xl p-[1.5px] bg-gradient-to-br from-violet-500/70 via-indigo-500/40 to-cyan-400/70 vg-glow"
            data-testid="developer-card"
          >
            <div className="rounded-3xl bg-[#0C0E15] px-6 py-8 sm:px-10 text-center relative overflow-hidden">
              <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-violet-600/20 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-cyan-500/15 blur-3xl pointer-events-none" />

              <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-400 px-3 py-1 rounded-full bg-white/5 border border-white/10">
                <Code2 size={13} className="text-cyan-400" /> {t("dev.by")}
              </span>

              <h4 className="mt-4 text-3xl sm:text-4xl font-head font-extrabold tracking-tight vg-gradient-text">
                Jakir Momin
              </h4>

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="tel:+918463860303"
                  className="group flex items-center gap-2.5 w-full sm:w-auto px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-violet-500/50 hover:bg-white/10 transition-all"
                  data-testid="developer-phone"
                >
                  <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 grid place-items-center shrink-0">
                    <Phone size={15} className="text-white" />
                  </span>
                  <span className="text-left">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-500">{t("dev.mobile")}</span>
                    <span className="block text-sm font-mono text-white">+91 84638 60303</span>
                  </span>
                </a>

                <a
                  href="mailto:mdjkbaba786@gmail.com"
                  className="group flex items-center gap-2.5 w-full sm:w-auto px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/50 hover:bg-white/10 transition-all"
                  data-testid="developer-email"
                >
                  <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 grid place-items-center shrink-0">
                    <Mail size={15} className="text-white" />
                  </span>
                  <span className="text-left">
                    <span className="block text-[10px] uppercase tracking-widest text-slate-500">{t("dev.email")}</span>
                    <span className="block text-sm font-mono text-white">mdjkbaba786@gmail.com</span>
                  </span>
                </a>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-slate-600 text-center mt-8">
            © {new Date().getFullYear()} VidGrab · {t("about.rights")}
          </p>
        </div>
      </footer>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialTab={authTab} key={authTab} />
      <HistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        items={history}
        onChange={loadHistory}
      />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      <ChatAssistant />
    </div>
  );
};

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Shell />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <Toaster position="top-center" theme="dark" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
